import { useState, useEffect } from 'react'
import { Row, Col, Spinner } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'
import { Formik, Form as FormikForm, Field } from 'formik'
import * as Yup from 'yup'
import {
  FaBuilding,
  FaSave,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaIdCard,
  FaFileInvoice,
  FaEdit,
  FaTimes,
  FaShieldAlt,
} from 'react-icons/fa'
import {
  fetchCompanyProfile,
  updateCompanyProfile,
} from '../../store/slices/companySlice'
import '../../assets/scss/pages/CompanyProfile.scss'

const validationSchema = Yup.object({
  company_code: Yup.string().required('Required'),
  company_name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').nullable(),
  phone: Yup.string().matches(/^[0-9]{10}$/, 'Must be 10 digits').nullable(),
  panNumber: Yup.string().matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN').nullable(),
  gstNumber: Yup.string().matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GST').nullable(),
})

const CompanyProfile = () => {
  const dispatch = useDispatch()
  const { companyProfile, loading } = useSelector(state => state.company)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    dispatch(fetchCompanyProfile())
  }, [dispatch])

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await dispatch(updateCompanyProfile(values)).unwrap()
      setIsEditing(false)
    } catch {
      // handled in slice
    } finally {
      setSubmitting(false)
    }
  }

  const initials = companyProfile?.company_name
    ? companyProfile.company_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : 'CO'

  return (
    <div className="cp-page">
      <div className="cp-header">
        <div className="cp-header-left">
          <div className="cp-header-icon"><FaBuilding /></div>
          <div>
            <h1 className="cp-title">Company Profile</h1>
            <p className="cp-sub">Manage your organisation&apos;s details and compliance information</p>
          </div>
        </div>
        <div className="cp-header-right">
          {!isEditing && (
            <button className="cp-btn-edit" onClick={() => setIsEditing(true)}>
              <FaEdit className="me-2" />Edit Profile
            </button>
          )}
        </div>
      </div>

      {loading
        ? (
          <div className="cp-loading">
            <Spinner animation="border" size="sm" className="me-2" />Loading profile…
          </div>
        )
        : (
          <Formik
            initialValues={{
              company_code: companyProfile?.company_code || '',
              company_name: companyProfile?.company_name || '',
              email: companyProfile?.email || '',
              phone: companyProfile?.phone || '',
              address: companyProfile?.address || '',
              panNumber: companyProfile?.panNumber || '',
              gstNumber: companyProfile?.gstNumber || '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ errors, touched, isSubmitting }) => (
              <FormikForm>
                <div className="cp-layout">

                  {/* LEFT: Identity Card */}
                  <div className="cp-identity-card">
                    <div className="cp-avatar">{initials}</div>
                    <div className="cp-id-name">{companyProfile?.company_name || '—'}</div>
                    <div className="cp-id-code">{companyProfile?.company_code || '—'}</div>
                    <div className="cp-id-type">{companyProfile?.companyType || 'Commercial Logistics'}</div>
                    <div className="cp-id-divider" />
                    <div className="cp-id-row"><FaEnvelope className="cp-id-icon" /><span>{companyProfile?.email || '—'}</span></div>
                    <div className="cp-id-row"><FaPhone className="cp-id-icon" /><span>{companyProfile?.phone || '—'}</span></div>
                    <div className="cp-id-row"><FaMapMarkerAlt className="cp-id-icon" /><span>{companyProfile?.address || '—'}</span></div>
                    <div className="cp-id-row"><FaIdCard className="cp-id-icon" /><span>{companyProfile?.panNumber || '—'}</span></div>
                    <div className="cp-id-row"><FaFileInvoice className="cp-id-icon" /><span>{companyProfile?.gstNumber || '—'}</span></div>
                  </div>

                  {/* RIGHT: Form Card */}
                  <div className="cp-form-card">

                    <div className="cp-section">
                      <div className="cp-section-title">Basic Information</div>
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="cp-form-group">
                            <label className="cp-label"><FaShieldAlt className="cp-lbl-icon" /> Company Code</label>
                            <Field name="company_code" className={`cp-input cp-input--mono ${touched.company_code && errors.company_code ? 'cp-input--error' : ''}`} disabled placeholder="e.g. TS001" />
                            {touched.company_code && errors.company_code && <span className="cp-error">{errors.company_code}</span>}
                            <span className="cp-hint">Immutable system identifier</span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="cp-form-group">
                            <label className="cp-label"><FaBuilding className="cp-lbl-icon" /> Company Name <span className="cp-required">*</span></label>
                            <Field name="company_name" className={`cp-input ${touched.company_name && errors.company_name ? 'cp-input--error' : ''}`} disabled={!isEditing} placeholder="Organisation full name" />
                            {touched.company_name && errors.company_name && <span className="cp-error">{errors.company_name}</span>}
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="cp-section">
                      <div className="cp-section-title">Contact Details</div>
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="cp-form-group">
                            <label className="cp-label"><FaEnvelope className="cp-lbl-icon" /> Email Address</label>
                            <Field name="email" type="email" className={`cp-input ${touched.email && errors.email ? 'cp-input--error' : ''}`} disabled={!isEditing} placeholder="official@company.com" />
                            {touched.email && errors.email && <span className="cp-error">{errors.email}</span>}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="cp-form-group">
                            <label className="cp-label"><FaPhone className="cp-lbl-icon" /> Phone Number</label>
                            <Field name="phone" className={`cp-input ${touched.phone && errors.phone ? 'cp-input--error' : ''}`} disabled={!isEditing} placeholder="+91 00000 00000" />
                            {touched.phone && errors.phone && <span className="cp-error">{errors.phone}</span>}
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="cp-form-group">
                            <label className="cp-label"><FaMapMarkerAlt className="cp-lbl-icon" /> Address</label>
                            <Field as="textarea" name="address" rows={3} className="cp-input cp-textarea" disabled={!isEditing} placeholder="Full registered address with city, state, pincode" />
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="cp-section cp-section--last">
                      <div className="cp-section-title">Tax &amp; Compliance</div>
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="cp-form-group">
                            <label className="cp-label"><FaIdCard className="cp-lbl-icon" /> PAN Number</label>
                            <Field name="panNumber" className={`cp-input cp-input--mono ${touched.panNumber && errors.panNumber ? 'cp-input--error' : ''}`} disabled={!isEditing} placeholder="ABCDE1234F" style={{ textTransform: 'uppercase' }} />
                            {touched.panNumber && errors.panNumber && <span className="cp-error">{errors.panNumber}</span>}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="cp-form-group">
                            <label className="cp-label"><FaFileInvoice className="cp-lbl-icon" /> GST Number</label>
                            <Field name="gstNumber" className={`cp-input cp-input--mono ${touched.gstNumber && errors.gstNumber ? 'cp-input--error' : ''}`} disabled={!isEditing} placeholder="22ABCDE1234F1Z5" style={{ textTransform: 'uppercase' }} />
                            {touched.gstNumber && errors.gstNumber && <span className="cp-error">{errors.gstNumber}</span>}
                          </div>
                        </Col>
                      </Row>
                    </div>

                    {isEditing && (
                      <div className="cp-form-footer">
                        <span className="cp-footer-note"><span className="cp-required">*</span> Required field</span>
                        <div className="cp-footer-actions">
                          <button type="button" className="cp-btn-cancel" onClick={() => setIsEditing(false)} disabled={isSubmitting}>
                            <FaTimes className="me-2" />Cancel
                          </button>
                          <button type="submit" className="cp-btn-save" disabled={isSubmitting}>
                            {isSubmitting
                              ? <><Spinner size="sm" animation="border" className="me-2" />Saving…</>
                              : <><FaSave className="me-2" />Save Changes</>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </FormikForm>
            )}
          </Formik>
        )}
    </div>
  )
}

export default CompanyProfile
